/**
 * HashCash proof-of-work implementation.
 * Vendored from @vdevs/hash-cash (original by Andi Gotzler).
 * The `init()` function was omitted as it depends on @elements/* packages
 * that are not part of this project.
 */

function rightRotate(value: number, amount: number): number {
    return (value >>> amount) | (value << (32 - amount));
}

function sha256(ascii: string): string {
    const mathPow = Math.pow;
    const maxWord = mathPow(2, 32);
    const words: number[] = [];
    const asciiBitLength: number = ascii.length * 8;

    let i, j;
    let result = '';

    const fn = sha256 as any;
    if (!fn.h) fn.h = [];
    if (!fn.k) fn.k = [];

    let hash = fn.h;
    const k = fn.k;
    let primeCounter = k.length;

    const isComposite: { [key: number]: number } = {};
    for (let candidate = 2; primeCounter < 64; candidate++) {
        if (!isComposite[candidate]) {
            for (let i = 0; i < 313; i += candidate) {
                isComposite[i] = candidate;
            }
            hash[primeCounter] = (mathPow(candidate, .5) * maxWord) | 0;
            k[primeCounter++] = (mathPow(candidate, 1 / 3) * maxWord) | 0;
        }
    }

    ascii += '\x80';
    while ((ascii.length % 64) - 56 !== 0) ascii += '\x00';
    for (let i = 0; i < ascii.length; i++) {
        const j = ascii.charCodeAt(i);
        if (j > 255) return '';
        words[i >> 2] = (words[i >> 2] || 0) | (j << ((3 - i) % 4) * 8);
    }
    words[words.length] = ((asciiBitLength / maxWord) | 0);
    words[words.length] = (asciiBitLength);

    for (j = 0; j < words.length;) {
        const w = words.slice(j, j += 16);
        const oldHash = hash;
        hash = hash.slice(0, 8);

        for (i = 0; i < 64; i++) {
            const w15 = w[i - 15], w2 = w[i - 2];
            const a = hash[0], e = hash[4];
            const temp1 = hash[7]
                + (rightRotate(e, 6) ^ rightRotate(e, 11) ^ rightRotate(e, 25))
                + ((e & hash[5]) ^ ((~e) & hash[6]))
                + k[i]
                + (w[i] = (i < 16) ? w[i] : (
                    w[i - 16]
                    + (rightRotate(w15, 7) ^ rightRotate(w15, 18) ^ (w15 >>> 3))
                    + w[i - 7]
                    + (rightRotate(w2, 17) ^ rightRotate(w2, 19) ^ (w2 >>> 10))
                ) | 0);
            const temp2 = (rightRotate(a, 2) ^ rightRotate(a, 13) ^ rightRotate(a, 22))
                + ((a & hash[1]) ^ (a & hash[2]) ^ (hash[1] & hash[2]));

            hash = [(temp1 + temp2) | 0].concat(hash);
            hash[4] = (hash[4] + temp1) | 0;
        }

        for (i = 0; i < 8; i++) {
            hash[i] = (hash[i] + oldHash[i]) | 0;
        }
    }

    for (i = 0; i < 8; i++) {
        for (j = 3; j + 1; j--) {
            const b = (hash[i] >> (j * 8)) & 255;
            result += ((b < 16) ? 0 : '') + b.toString(16);
        }
    }
    return result;
}

function hexInBin(x: string): string {
    switch (x.toUpperCase()) {
        case '0': return '0000';
        case '1': return '0001';
        case '2': return '0010';
        case '3': return '0011';
        case '4': return '0100';
        case '5': return '0101';
        case '6': return '0110';
        case '7': return '0111';
        case '8': return '1000';
        case '9': return '1001';
        case 'A': return '1010';
        case 'B': return '1011';
        case 'C': return '1100';
        case 'D': return '1101';
        case 'E': return '1110';
        case 'F': return '1111';
        default:  return '0000';
    }
}

function extractBits(hex_string: string, num_bits: number): string {
    let bit_string = '';
    const num_chars = Math.ceil(num_bits / 4);
    for (let i = 0; i < num_chars; i++) {
        bit_string += hexInBin(hex_string.charAt(i));
    }
    return bit_string.slice(0, num_bits);
}

function checkNonce(difficulty: number, stamp: string, nonce: number): boolean {
    const col_hash = sha256(stamp + nonce);
    const check_bits = extractBits(col_hash, difficulty);
    return parseInt(check_bits, 10) === 0;
}

function setFormData(form: HTMLFormElement, inputName: string, inputValue: string): void {
    const input = form.querySelector<HTMLInputElement>(`[name="${inputName}"]`);
    if (input) input.value = inputValue;
}

function getFormData(form: HTMLFormElement, inputName: string): string {
    return form.querySelector<HTMLInputElement>(`[name="${inputName}"]`)?.value ?? '';
}

async function findHash(form: HTMLFormElement): Promise<boolean> {
    const hasStamp = form.querySelector<HTMLInputElement>('input[name="vfbhc_stamp"]');
    const hasDifficulty = form.querySelector<HTMLInputElement>('input[name="vfbhc_difficulty"]');
    const hasNonce = form.querySelector<HTMLInputElement>('input[name="vfbhc_nonce"]');

    if (!hasStamp || !hasDifficulty || !hasNonce) {
        dispatchHashCashEvent(form, 'failed.hash-cash', false, 'HashCash: Required input fields not found.');
        return false;
    }

    const stamp = getFormData(form, 'vfbhc_stamp');
    const difficulty = parseInt(getFormData(form, 'vfbhc_difficulty'), 10);
    const existingNonce = parseInt(getFormData(form, 'vfbhc_nonce'), 10);

    if (existingNonce && checkNonce(difficulty, stamp, existingNonce)) {
        return true;
    }

    let nonce = 1;
    while (!checkNonce(difficulty, stamp, nonce)) {
        nonce++;
        if (nonce % 10000 === 0) {
            await new Promise(resolve => setTimeout(resolve, 100));
        }
    }

    setFormData(form, 'vfbhc_nonce', String(nonce));
    return true;
}

function dispatchHashCashEvent(form: HTMLFormElement, event: string, isValid: boolean, message = ''): void {
    if (!isValid) {
        console.error('HashCash Event:', { event, message });
    }
    form.dispatchEvent(new CustomEvent(event, {
        bubbles: true,
        detail: { valid: isValid, submitForm: true, message },
    }));
}

function submitHashCashForm(form: HTMLFormElement): void {
    findHash(form).then((isValid) => {
        const newAction = form.getAttribute('data-action') ?? '';

        if (form.hasAttribute('data-action')) {
            form.action = newAction;
        }

        if (isValid) {
            dispatchHashCashEvent(form, 'success.hash-cash', true);
            if (form.classList.contains('js-vfb-ajax-form')) {
                // Ajax mode: POST via fetch and report the server HTML through
                // vfb:ajax-success / vfb:ajax-error instead of a full-page submit.
                void submitFormAjax(form);
            } else {
                form.submit();
            }
        } else {
            dispatchHashCashEvent(form, 'failed.hash-cash', false, 'HashCash: Validation failed.');
        }

        // The submit buttons stay disabled on purpose: `form.submit()` navigates
        // asynchronously and `submitFormAjax` is still in flight here. Re-enabling
        // at this point made double submissions possible. The lock is released
        // exclusively by the abort/error events FormContainer listens to
        // (failed.hash-cash, form-guard.cancel, vfb:ajax-error).
    });
}

// ---------------------------------------------------------------------------
// Ajax submit
//
// When the form carries the `js-vfb-ajax-form` class (set by FormContainer in
// ajax mode), the submission is sent via fetch instead of a native form submit.
// The server answers with JSON `{ status: 'success' | 'error', html }` where the
// HTML is the success snippet (success) or the rendered validation errors
// (error). The result is published as bubbling DOM events so the Vue layer can
// inject the HTML and reset/hide the form.
// ---------------------------------------------------------------------------

const AJAX_FALLBACK_ERROR_HTML =
    '<div class="alert alert-danger" role="alert">An error occurred. Please try again.</div>';

export async function submitFormAjax(form: HTMLFormElement): Promise<void> {
    const dispatch = (event: 'vfb:ajax-success' | 'vfb:ajax-error', html: string): void => {
        form.dispatchEvent(new CustomEvent(event, { bubbles: true, detail: { html } }));
    };

    try {
        const response = await fetch(form.action, {
            method: 'POST',
            body: new FormData(form),
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        const data = await response.json() as { status?: string; html?: string };
        const html = data.html ?? '';
        if (data.status === 'error') {
            dispatch('vfb:ajax-error', html || AJAX_FALLBACK_ERROR_HTML);
        } else {
            dispatch('vfb:ajax-success', html);
        }
    } catch (err) {
        console.error('Ajax submit failed', err);
        dispatch('vfb:ajax-error', AJAX_FALLBACK_ERROR_HTML);
    }
}

// ---------------------------------------------------------------------------
// Form-Guard (ported from bundles/form-builder/public/js/hashCash.js)
//
// Writes the hidden inputs `vfbhc_form-guard_elapsed-time` and
// `vfbhc_form-guard_confirmed` that HashCashService expects. Without these
// the backend rejects every submission from the web component.
// ---------------------------------------------------------------------------

interface FormGuardOptions {
    enableFormGuard: boolean;
    formGuardThresholdTime: number;
}

const DEFAULT_FORM_GUARD_OPTIONS: FormGuardOptions = {
    enableFormGuard: false,
    formGuardThresholdTime: 2000,
};

const firstInteractionTime = new WeakMap<HTMLFormElement, number>();
const armedForms = new WeakSet<HTMLFormElement>();

function ensureHiddenInput(form: HTMLFormElement, name: string): HTMLInputElement {
    let input = form.querySelector<HTMLInputElement>(`input[name="${name}"]`);
    if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        form.appendChild(input);
    }
    return input;
}

function dispatchFormGuardEvent(form: HTMLFormElement, name: string, detail: Record<string, unknown>): void {
    form.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
}

export function armFormGuardListener(form: HTMLFormElement): void {
    if (armedForms.has(form)) return;
    armedForms.add(form);

    const onFirstInteraction = () => {
        if (!firstInteractionTime.has(form)) {
            firstInteractionTime.set(form, Date.now());
        }
    };

    const types: Array<keyof HTMLElementEventMap> = [
        'input', 'change', 'focusin', 'keydown', 'pointerdown', 'click',
    ];
    const options: AddEventListenerOptions = { passive: true, capture: true };
    types.forEach(type => form.addEventListener(type, onFirstInteraction, options));
}

async function loadBootstrapModal(): Promise<any> {
    const globalCtor = (window as any).bootstrap?.Modal;
    if (globalCtor) return globalCtor;
    try {
        const mod: any = await import('bootstrap/js/dist/modal');
        return mod?.default ?? mod ?? null;
    } catch {
        return null;
    }
}

async function presentFormGuardModal(): Promise<boolean> {
    const locale = document.documentElement.lang || 'de';
    const url = `/${locale}/vfb/ajax/form-guard-modal`;

    let response: Response;
    try {
        response = await fetch(url);
    } catch {
        return false;
    }
    if (!response.ok) {
        console.error('[vfb] FormGuard: AJAX request failed', response.statusText);
        return false;
    }

    const data = await response.json() as { html?: string };
    const parsed = new DOMParser().parseFromString(data.html ?? '', 'text/html');
    const parsedRoot = parsed.body.querySelector<HTMLElement>('.js-vfbhc-form-guard-dialog');
    if (!parsedRoot) {
        console.error('[vfb] FormGuard: no modal element in AJAX response');
        return false;
    }
    const modalRoot = document.importNode(parsedRoot, true) as HTMLElement;
    document.body.appendChild(modalRoot);

    const modalCtor = await loadBootstrapModal();
    if (!modalCtor) {
        console.warn('[vfb] Bootstrap Modal not available – cannot show form-guard modal');
        modalRoot.remove();
        return false;
    }

    const modalCheckbox = modalRoot.querySelector<HTMLInputElement>('.js-vfbhc-form-guard-dialog__checkbox');
    const modalConfirmBtn = modalRoot.querySelector<HTMLButtonElement>('.js-vfbhc-form-guard-dialog__confirm');
    const modalCloseBtn = modalRoot.querySelector<HTMLButtonElement>('.js-vfbhc-form-guard-dialog__cancel');

    return new Promise<boolean>((resolve) => {
        let finished = false;
        const finish = (ok: boolean) => {
            if (finished) return;
            finished = true;
            cleanup();
            const isConfirmed = ok && (modalCheckbox ? modalCheckbox.checked : true);
            modalRoot.remove();
            resolve(isConfirmed);
        };

        const toggleConfirmBtnEnabled = () => {
            if (modalConfirmBtn && modalCheckbox) {
                modalConfirmBtn.disabled = !modalCheckbox.checked;
            }
        };
        const onConfirm = (e: Event) => {
            e.preventDefault();
            if (modalCheckbox && !modalCheckbox.checked) return;
            modalInstance.hide();
            finish(true);
        };
        const onCancel = (e: Event) => {
            e.preventDefault();
            modalInstance.hide();
            finish(false);
        };
        const onHidden = () => finish(false);
        const cleanup = () => {
            modalCheckbox?.removeEventListener('change', toggleConfirmBtnEnabled);
            modalConfirmBtn?.removeEventListener('click', onConfirm);
            modalCloseBtn?.removeEventListener('click', onCancel);
            modalRoot.removeEventListener('hidden.bs.modal', onHidden);
        };

        modalCheckbox?.addEventListener('change', toggleConfirmBtnEnabled);
        modalConfirmBtn?.addEventListener('click', onConfirm);
        modalCloseBtn?.addEventListener('click', onCancel);

        const modalInstance = modalCtor.getOrCreateInstance(modalRoot);
        modalRoot.addEventListener('hidden.bs.modal', onHidden, { once: true });
        modalInstance.show();
        setTimeout(() => modalCheckbox?.focus(), 0);
    });
}

async function runFormGuardGate(form: HTMLFormElement): Promise<boolean> {
    const enableByData = form.getAttribute('data-vfbhc-enable-form-guard');
    const thresholdByData = form.getAttribute('data-vfbhc-form-guard-threshold-time');
    const opts: FormGuardOptions = {
        enableFormGuard: enableByData !== null
            ? (enableByData === 'true' || enableByData === '1')
            : DEFAULT_FORM_GUARD_OPTIONS.enableFormGuard,
        formGuardThresholdTime: thresholdByData !== null
            ? parseInt(thresholdByData, 10)
            : DEFAULT_FORM_GUARD_OPTIONS.formGuardThresholdTime,
    };

    const start = firstInteractionTime.get(form);
    const elapsed = start ? Date.now() - start : 0;

    ensureHiddenInput(form, 'vfbhc_form-guard_elapsed-time').value = String(elapsed);
    const confirmInput = ensureHiddenInput(form, 'vfbhc_form-guard_confirmed');

    if (!opts.enableFormGuard || elapsed >= opts.formGuardThresholdTime) {
        confirmInput.value = '1';
        return true;
    }

    const ok = await presentFormGuardModal();
    if (ok) {
        dispatchFormGuardEvent(form, 'form-guard.open', { elapsedMs: elapsed });
    }
    confirmInput.value = ok ? '1' : '0';
    dispatchFormGuardEvent(form, ok ? 'form-guard.confirm' : 'form-guard.cancel', { elapsedMs: elapsed });
    return ok;
}

export function handleAjaxHashCash(submittedForm: HTMLFormElement): void {
    runFormGuardGate(submittedForm).then((proceed) => {
        if (!proceed) {
            // runFormGuardGate already dispatched form-guard.cancel — FormContainer
            // releases the submit lock on that event.
            return;
        }
        fetch('/vfb/ajax/create-stamp?uri=' + encodeURI(location.href) + '&time=' + Date.now())
            .then(r => r.json())
            .then((data: Record<string, string>) => {
                for (const property in data) {
                    const input = document.createElement('input');
                    input.type = 'hidden';
                    input.name = property;
                    input.value = data[property];
                    submittedForm.appendChild(input);
                }
                submitHashCashForm(submittedForm);
            })
            .catch((err) => {
                console.error('HashCash: Fetch Error', err);
                // Nothing was submitted — report the abort so the submit lock is
                // released instead of leaving the button dead.
                dispatchHashCashEvent(
                    submittedForm,
                    'failed.hash-cash',
                    false,
                    'HashCash: stamp request failed.',
                );
            });
    });
}
